import React, { useCallback, useEffect, useState } from 'react';
import { FlexWrapper, GrapBox, HighlightText, PaginationBox } from '../../theme/common_style';
import { Button, Pagination, Table } from 'antd';
import {
  checkPermission,
  debounce,
  generateEmployeeImgUrl,
  getFullName
} from '../../utils/common_functions';
import { useSelector } from 'react-redux';
import SearchField from '../../components/searchField/SearchField';
import TableLoader from '../../components/loaders/TableLoader';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import FilterDrawer from './FilterDrawer.js';
import { StickyBox } from '../../utils/style';
import { AvatarGroup } from '../../components/common/AvatarGroup';
import FilterButton from '../../components/common/FilterButton';
import { useCurrentModule } from '../../hooks/useCurrentModule';
import ProjectsListingModal from '../../components/Modal/ProjectListingModal';
import { getEmployeesWithSalary, getTotalSalaryGraph } from '../../services/api_collection.js';
import dayjs from 'dayjs';
import { currency } from '../../utils/constant.js';
import BarGraph from '../../components/common/BarGraph.js';
import styled from '@emotion/styled';

const SalaryRecord = () => {
  const { permissions } = useSelector((state) => state?.userInfo?.data);
  const moduleName = useCurrentModule();
  const canCreate = checkPermission(moduleName, 'create', permissions);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const navigate = useNavigate();
  const [search, setSearch] = useState(null);
  const [filterDrawer, setFilterDrawer] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState(null);
  const [sort, setSort] = useState({});
  const [projectList, setProjectList] = useState(false);
  const [projectListData, setProjectListData] = useState([]);

  const handleSorting = (val) => {
    if (val?.field) {
      let order = val?.order == 'ascend' ? 'ASC' : 'DESC';
      setSort({ sortBy: val?.field, orderBy: order, ...val });
    } else {
      setSort({});
    }
    setPage(page);
  };

  const returnDate = (date) => {
    if (!date) return 'N/A';
    return dayjs(date).format('DD-MM-YYYY');
  };

  const columns = [
    {
      title: 'S.NO',
      dataIndex: 'key',
      key: 'key',
      render: (text) => <span>{(page - 1) * limit + text}.</span>
    },
    {
      title: 'Emp Name',
      dataIndex: 'first_name',
      key: 'first_name',
      sorter: true,
      render: (_, data) => {
        const empName = getFullName(data?.first_name, data?.middle_name, data?.last_name);
        const empData = [
          {
            name: empName,
            src: generateEmployeeImgUrl(data?.id),
            id: data?.id,
            completionColor: data?.profile_completion
          }
        ];
        return (
          <FlexWrapper
            justify={'start'}
            gap={'6px'}
            wrap={'unset'}
            onClick={() =>
              navigate(`/view-employee`, {
                state: { name: empName, id: data?.id }
              })
            }>
            <AvatarGroup avatars={empData} />
            <HighlightText>{empName}</HighlightText>
          </FlexWrapper>
        );
      }
    },
    {
      title: 'Emp Code',
      dataIndex: 'emp_code',
      key: 'emp_code',
      sorter: true
    },
    {
      title: 'Basic Salary',
      dataIndex: 'basic_salary',
      key: 'basic_salary',
      sorter: true,
      render: (_, record) => (
        <span>
          {record?.salaries?.[0]?.basic_salary
            ? currency.INR + record?.salaries?.[0]?.basic_salary
            : 'N/A'}
        </span>
      )
    },
    {
      title: 'Inhand Salary',
      dataIndex: 'inhand_salary',
      key: 'inhand_salary',
      sorter: true,
      render: (_, record) => (
        <span>
          {record?.salaries?.[0]?.inhand_salary
            ? currency.INR + record?.salaries?.[0]?.inhand_salary
            : 'N/A'}
        </span>
      )
    },
    {
      title: 'Effective Date',
      dataIndex: 'effective_date',
      key: 'effective_date',
      sorter: true,
      render: (_, record) => <span>{returnDate(record?.salaries?.[0]?.effective_date)}</span>
    },
    {
      title: 'Next review Date',
      dataIndex: 'next_review_date',
      key: 'next_review_date',
      sorter: true,
      render: (_, record) => <span>{returnDate(record?.salaries?.[0]?.next_review_date)}</span>
    }
  ];
  const handlePageChange = (page) => {
    setPage(page);
  };

  const handleGetAllEmployees = async (search) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit, ...(search && { search }) });
      sort?.sortBy && params.append('sort_column', sort?.sortBy);
      sort?.orderBy && params.append('sort_order', sort?.orderBy);
      Object.entries(appliedFilter || {}).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value?.length > 0 && params.append(key, value);
        } else {
          value && params.append(key, value);
        }
      });

      const res = await getEmployeesWithSalary(params);
      if (res?.statusCode === 200) {
        setTotal(res?.data?.total);
        setDataSource(
          res?.data?.list?.map((el, idx) => ({
            ...el,
            key: idx + 1
          }))
        );
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (error) {
      toast.error(error?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const optimizedFn = useCallback(debounce(handleGetAllEmployees), [appliedFilter, sort, page]);

  useEffect(() => {
    if (search !== null) {
      optimizedFn(search);
    } else {
      handleGetAllEmployees();
    }
  }, [search, page, appliedFilter, sort]);

  return (
    <div style={{ position: 'relative' }}>
      {projectList && (
        <ProjectsListingModal
          open={projectList}
          onClose={() => {
            setProjectList(false);
            setProjectListData([]);
          }}
          data={projectListData}
        />
      )}
      {filterDrawer && (
        <FilterDrawer
          open={filterDrawer}
          onClose={() => setFilterDrawer(false)}
          setAppliedFilter={setAppliedFilter}
          appliedFilter={appliedFilter}
        />
      )}
      <GrapBox width="900px">
        <div className="chart-wrapper">
          <BarGraph title="Total Salaries" api={getTotalSalaryGraph} sign={currency.INR} />
        </div>
      </GrapBox>
      <StickyBox>
        <FlexWrapper width="100%" justify={canCreate ? 'space-between' : 'end'} gap="16px">
          {canCreate && (
            <Button prefixCls="antCustomBtn" onClick={() => setCreateModal(true)}>
              + Add New Employee
            </Button>
          )}
          <FlexWrapperBox>
            <SearchField
              placeholder="Search by Emp Name..."
              width={'250px'}
              onChange={(val) => {
                setSearch(val);
                setPage(1);
              }}
              allowClear
              value={search}
            />
            <FilterButton handleClick={setFilterDrawer} appliedFilter={appliedFilter} />
          </FlexWrapperBox>
        </FlexWrapper>
      </StickyBox>
      <Table
        prefixCls="antCustomTable"
        columns={columns}
        dataSource={dataSource || []}
        pagination={false}
        loading={{ spinning: loading, indicator: TableLoader }}
        onChange={(_, filters, sorter) => {
          handleSorting(sorter);
        }}
        defaultSortOrder={sort.order}
      />
      {total > limit && (
        <PaginationBox>
          <Pagination
            current={page}
            prefixCls="custom-pagination"
            pageSize={limit}
            total={total}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </PaginationBox>
      )}
    </div>
  );
};

export default SalaryRecord;

const FlexWrapperBox = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  @media (max-width: 668px) {
    width: 100%;
    display: grid;
    grid-template-columns: 70% 30%;
    gap: 5px;
  }
`;
